import { useState } from "react";
import { Link2, ExternalLink, Pencil, Save, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { setDispatchTrackingUrl } from "@/hooks/useUpdateDispatchStatus";

interface Props {
  dispatchId: string;
  loadId: string;
  trackingUrl: string | null;
  trackingUrlUpdatedAt: string | null;
  editable: boolean;
  actorId: string;
  counterpartyUserId: string | null;
  shipperUserId: string | null;
  onChanged?: () => void;
  /** Optional visual tone classes (fill/border/shadow). Defaults to unchanged styling. */
  cardClassName?: string;
}

function relativeAgo(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function DispatchTrackingLinkCard(props: Props) {
  const {
    dispatchId, loadId, trackingUrl, trackingUrlUpdatedAt,
    editable, actorId, counterpartyUserId, shipperUserId, onChanged, cardClassName,
  } = props;


  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(trackingUrl ?? "");
  const [busy, setBusy] = useState(false);

  const startEditing = () => {
    setValue(trackingUrl ?? "");
    setEditing(true);
  };

  const save = async (newUrl: string | null) => {
    setBusy(true);
    try {
      await setDispatchTrackingUrl({
        dispatchId, loadId, newUrl,
        actorId, counterpartyUserId, shipperUserId,
      });
      toast.success(newUrl ? "Tracking link saved" : "Tracking link cleared");
      setEditing(false);
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.message || "Could not update tracking link");
    } finally {
      setBusy(false);
    }
  };

  // Shipper view — hide entirely when no link and not editable.
  if (!editable && !trackingUrl) return null;

  return (
    <Card className={`p-3 mb-4 ${cardClassName ?? ""}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="h-4 w-4 text-brand-teal shrink-0" />
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
            External Tracking Link
          </div>
        </div>
        {editable && !editing && (
          <Button size="sm" variant="ghost" className="h-6 gap-1 text-[11px] px-2" onClick={startEditing}>
            <Pencil className="h-3 w-3" /> {trackingUrl ? "Edit" : "Add"}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <Input
            type="url"
            placeholder="https://tracking.macropoint.com/..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-8 text-xs"
            autoFocus
          />
          <p className="text-[10px] text-muted-foreground">
            Paste any external tracking URL (Macropoint, Trucker Tools, project44, etc.). Must start with http:// or https://.
          </p>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              className="h-7 text-xs flex-1 gap-1 bg-brand-teal hover:bg-brand-teal/90 text-white"
              onClick={() => save(value.trim() || null)}
              disabled={busy}
            >
              <Save className="h-3 w-3" /> Save
            </Button>
            {trackingUrl && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => save(null)} disabled={busy}>
                Clear
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setEditing(false)} disabled={busy}>
              <X className="h-3 w-3" /> Cancel
            </Button>
          </div>
        </div>
      ) : trackingUrl ? (
        <div className="space-y-1.5">
          <a
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-teal px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-teal/90 transition"
          >
            Track Shipment <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-muted-foreground truncate max-w-[240px]" title={trackingUrl}>
              {hostFromUrl(trackingUrl)}
            </span>
            <span className="text-[10px] text-muted-foreground italic">External link — opens in a new tab</span>
          </div>
          {trackingUrlUpdatedAt && (
            <Badge variant="outline" className="text-[10px] font-normal gap-1 border-border/60">
              <Clock className="h-2.5 w-2.5" /> updated {relativeAgo(trackingUrlUpdatedAt)}
            </Badge>
          )}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          No tracking link yet — paste one from your TMS/tracking provider so the shipper can watch the truck.
        </div>
      )}
    </Card>
  );
}
